import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate, formatDateTime, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Wrench,
  Search,
  Plus,
  Edit,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { useStore } from '../context/StoreContext';

export default function Services() {
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    device_type: '',
    device_brand: '',
    device_model: '',
    serial_number: '',
    problem_description: '',
    diagnosis: '',
    solution: '',
    parts_used: '',
    labor_cost: 0,
    parts_cost: 0,
    total_cost: 0,
    paid: 0,
    debt: 0,
    status: 'pending',
    warranty_months: 0,
    notes: '',
  });

  // Store context
  const { currentStore } = useStore();

  useEffect(() => {
    loadData();
  }, [currentStore]);

  const loadData = async () => {
    try {
      const storeParam = currentStore?.id ? `?store_id=${currentStore.id}` : '';
      const [servicesRes, customersRes] = await Promise.all([
        api.get(`/services${storeParam}`),
        api.get(`/customers${storeParam}`)
      ]);
      setServices(servicesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.customer_name.toLowerCase().includes(search.toLowerCase()) ||
                         service.device_brand.toLowerCase().includes(search.toLowerCase()) ||
                         service.serial_number?.includes(search);
    const matchesStatus = statusFilter === 'all' || service.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      total_cost: formData.labor_cost + formData.parts_cost,
      debt: Math.max(0, (formData.labor_cost + formData.parts_cost) - formData.paid)
    };
    
    if (currentStore?.id) {
      data.store_id = currentStore.id;
    }
    
    try {
      if (selectedService) {
        await api.put(`/services/${selectedService.id}`, data);
        toast.success('Servisi u përditësua');
      } else {
        await api.post('/services', data);
        toast.success('Servisi u regjistrua');
      }
      setIsModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const updateStatus = async (service, newStatus) => {
    try {
      const data = { ...service, status: newStatus };
      if (currentStore?.id) {
        data.store_id = currentStore.id;
      }
      await api.put(`/services/${service.id}`, data);
      toast.success('Statusi u përditësua');
      loadData();
    } catch (error) {
      toast.error('Gabim në përditësimin e statusit');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      customer_name: '',
      device_type: '',
      device_brand: '',
      device_model: '',
      serial_number: '',
      problem_description: '',
      diagnosis: '',
      solution: '',
      parts_used: '',
      labor_cost: 0,
      parts_cost: 0,
      total_cost: 0,
      paid: 0,
      debt: 0,
      status: 'pending',
      warranty_months: 0,
      notes: '',
    });
    setSelectedService(null);
  };

  const openEditModal = (service) => {
    setSelectedService(service);
    setFormData({
      customer_id: service.customer_id || '',
      customer_name: service.customer_name,
      device_type: service.device_type,
      device_brand: service.device_brand,
      device_model: service.device_model || '',
      serial_number: service.serial_number || '',
      problem_description: service.problem_description,
      diagnosis: service.diagnosis || '',
      solution: service.solution || '',
      parts_used: service.parts_used || '',
      labor_cost: service.labor_cost,
      parts_cost: service.parts_cost,
      total_cost: service.total_cost,
      paid: service.paid,
      debt: service.debt,
      status: service.status,
      warranty_months: service.warranty_months,
      notes: service.notes || '',
    });
    setIsModalOpen(true);
  };

  const openViewModal = (service) => {
    setSelectedService(service);
    setIsViewModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { variant: 'warning', icon: Clock, label: 'Në pritje', className: 'bg-orange-100 text-orange-800' },
      in_progress: { variant: 'info', icon: AlertCircle, label: 'Në punë', className: 'bg-violet-100 text-violet-800' },
      completed: { variant: 'success', icon: CheckCircle, label: 'Përfunduar', className: 'bg-lime-100 text-lime-800' },
      cancelled: { variant: 'destructive', icon: XCircle, label: 'Anuluar', className: 'bg-rose-100 text-rose-800' },
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <Badge className={badge.className}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </Badge>
    );
  };

  const deviceTypes = [
    'Laptop', 'Kompjuter Desktop', 'Telefon', 'Tablet', 'Printer', 
    'Monitor', 'TV', 'Pajisje tjeter'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="services-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Kërko servise..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-services"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Statusi" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Të gjitha</SelectItem>
              <SelectItem value="pending">Në pritje</SelectItem>
              <SelectItem value="in_progress">Në punë</SelectItem>
              <SelectItem value="completed">Përfunduar</SelectItem>
              <SelectItem value="cancelled">Anuluar</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }} data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-2" />
          Servis i Ri
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{services.filter(s => s.status === 'pending').length}</p>
            <p className="text-sm text-slate-500">Në pritje</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-bold text-violet-600">{services.filter(s => s.status === 'in_progress').length}</p>
            <p className="text-sm text-slate-500">Në punë</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-bold text-lime-600">{services.filter(s => s.status === 'completed').length}</p>
            <p className="text-sm text-slate-500">Përfunduar</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-bold font-mono text-rose-600">
              {formatCurrency(services.reduce((sum, s) => sum + (s.debt || 0), 0))}
            </p>
            <p className="text-sm text-slate-500">Borxh total</p>
          </CardContent>
        </Card>
      </div>

      {/* Services Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Klienti</TableHead>
                <TableHead>Pajisja</TableHead>
                <TableHead>Problemi</TableHead>
                <TableHead>Statusi</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Veprime</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    <Wrench className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    Nuk u gjet asnjë servis
                  </TableCell>
                </TableRow>
              ) : (
                filteredServices.map((service) => (
                  <TableRow key={service.id} data-testid={`service-row-${service.id}`}>
                    <TableCell className="font-mono text-sm">{formatDate(service.received_date)}</TableCell>
                    <TableCell className="font-medium">{service.customer_name}</TableCell>
                    <TableCell>{service.device_brand} {service.device_model}</TableCell>
                    <TableCell className="max-w-xs truncate">{service.problem_description}</TableCell>
                    <TableCell>{getStatusBadge(service.status)}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(service.total_cost)}
                      {service.debt > 0 && (
                        <div className="text-xs text-rose-500">Borxh: {formatCurrency(service.debt)}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openViewModal(service)} data-testid={`view-btn-${service.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(service)} data-testid={`edit-btn-${service.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={async () => {
                            if (window.confirm('Jeni të sigurt që doni të fshini këtë servis?')) {
                              try {
                                await api.delete(`/services/${service.id}`);
                                toast.success('Servisi u fshi');
                                loadData();
                              } catch (error) {
                                toast.error('Gabim në fshirje');
                              }
                            }
                          }}
                        >
                          <XCircle className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Service Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{selectedService ? 'Modifiko Servisin' : 'Servis i Ri'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Customer */}
              <div className="col-span-2">
                <Label>Klienti</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => {
                    const customer = customers.find(c => c.id === value);
                    setFormData({ 
                      ...formData, 
                      customer_id: value,
                      customer_name: customer?.name || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="service-customer-select">
                    <SelectValue placeholder="Zgjidh klientin" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Device Info */}
              <div>
                <Label>Lloji i pajisjes</Label>
                <Select
                  value={formData.device_type}
                  onValueChange={(value) => setFormData({ ...formData, device_type: value })}
                >
                  <SelectTrigger data-testid="device-type-select">
                    <SelectValue placeholder="Zgjidh llojin" />
                  </SelectTrigger>
                  <SelectContent>
                    {deviceTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Marka</Label>
                <Input
                  value={formData.device_brand}
                  onChange={(e) => setFormData({ ...formData, device_brand: e.target.value })}
                  required
                  data-testid="device-brand-input"
                />
              </div>
              <div>
                <Label>Modeli</Label>
                <Input
                  value={formData.device_model}
                  onChange={(e) => setFormData({ ...formData, device_model: e.target.value })}
                  data-testid="device-model-input"
                />
              </div>
              <div>
                <Label>Numri serik</Label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  className="font-mono"
                  data-testid="serial-number-input"
                />
              </div>

              {/* Problem */}
              <div className="col-span-2">
                <Label>Përshkrimi i problemit *</Label>
                <Textarea
                  value={formData.problem_description}
                  onChange={(e) => setFormData({ ...formData, problem_description: e.target.value })}
                  required
                  rows={2}
                  data-testid="problem-description-input"
                />
              </div>

              {/* Diagnosis & Solution (for editing) */}
              {selectedService && (
                <>
                  <div className="col-span-2">
                    <Label>Diagnoza</Label>
                    <Textarea
                      value={formData.diagnosis}
                      onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                      rows={2}
                      data-testid="diagnosis-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Zgjidhja</Label>
                    <Textarea
                      value={formData.solution}
                      onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                      rows={2}
                      data-testid="solution-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Pjesët e përdorura</Label>
                    <Input
                      value={formData.parts_used}
                      onChange={(e) => setFormData({ ...formData, parts_used: e.target.value })}
                      data-testid="parts-used-input"
                    />
                  </div>
                </>
              )}

              {/* Costs */}
              <div>
                <Label>Kosto punë (LEK)</Label>
                <Input
                  type="number"
                  value={formData.labor_cost}
                  onChange={(e) => setFormData({ ...formData, labor_cost: parseFloat(e.target.value) || 0 })}
                  className="font-mono"
                  data-testid="labor-cost-input"
                />
              </div>
              <div>
                <Label>Kosto pjesësh (LEK)</Label>
                <Input
                  type="number"
                  value={formData.parts_cost}
                  onChange={(e) => setFormData({ ...formData, parts_cost: parseFloat(e.target.value) || 0 })}
                  className="font-mono"
                  data-testid="parts-cost-input"
                />
              </div>
              <div>
                <Label>Paguar (LEK)</Label>
                <Input
                  type="number"
                  value={formData.paid}
                  onChange={(e) => setFormData({ ...formData, paid: parseFloat(e.target.value) || 0 })}
                  className="font-mono"
                  data-testid="service-paid-input"
                />
              </div>
              <div>
                <Label>Garanci (muaj)</Label>
                <Input
                  type="number"
                  value={formData.warranty_months}
                  onChange={(e) => setFormData({ ...formData, warranty_months: parseInt(e.target.value) || 0 })}
                  className="font-mono"
                  data-testid="warranty-months-input"
                />
              </div>

              {/* Status */}
              {selectedService && (
                <div className="col-span-2">
                  <Label>Statusi</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="service-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Në pritje</SelectItem>
                      <SelectItem value="in_progress">Në punë</SelectItem>
                      <SelectItem value="completed">Përfunduar</SelectItem>
                      <SelectItem value="cancelled">Anuluar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="col-span-2">
                <Label>Shënime</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="service-notes-input"
                />
              </div>
            </div>

            {/* Total display */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex justify-between text-lg font-bold">
                <span>TOTAL:</span>
                <span className="font-mono">{formatCurrency(formData.labor_cost + formData.parts_cost)}</span>
              </div>
              {formData.labor_cost + formData.parts_cost - formData.paid > 0 && (
                <div className="flex justify-between text-rose-600 mt-1">
                  <span>Borxh:</span>
                  <span className="font-mono">{formatCurrency(formData.labor_cost + formData.parts_cost - formData.paid)}</span>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Anulo
              </Button>
              <Button type="submit" data-testid="save-service-btn">
                Ruaj
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detajet e Servisit</DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-slate-500">Klienti</p>
                  <p className="font-semibold">{selectedService.customer_name}</p>
                </div>
                {getStatusBadge(selectedService.status)}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Pajisja</p>
                  <p className="font-medium">{selectedService.device_brand} {selectedService.device_model}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Lloji</p>
                  <p className="font-medium">{selectedService.device_type}</p>
                </div>
                {selectedService.serial_number && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Numri serik</p>
                    <p className="font-mono">{selectedService.serial_number}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm text-slate-500">Problemi</p>
                <p>{selectedService.problem_description}</p>
              </div>

              {selectedService.diagnosis && (
                <div>
                  <p className="text-sm text-slate-500">Diagnoza</p>
                  <p>{selectedService.diagnosis}</p>
                </div>
              )}

              {selectedService.solution && (
                <div>
                  <p className="text-sm text-slate-500">Zgjidhja</p>
                  <p>{selectedService.solution}</p>
                </div>
              )}

              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Kosto punë:</span>
                  <span className="font-mono">{formatCurrency(selectedService.labor_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Kosto pjesësh:</span>
                  <span className="font-mono">{formatCurrency(selectedService.parts_cost)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span className="font-mono">{formatCurrency(selectedService.total_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Paguar:</span>
                  <span className="font-mono">{formatCurrency(selectedService.paid)}</span>
                </div>
                {selectedService.debt > 0 && (
                  <div className="flex justify-between text-rose-600">
                    <span>Borxh:</span>
                    <span className="font-mono font-bold">{formatCurrency(selectedService.debt)}</span>
                  </div>
                )}
              </div>

              {selectedService.warranty_months > 0 && (
                <div className="bg-lime-50 p-3 rounded-lg text-center">
                  <p className="text-lime-800">Garanci: <span className="font-bold">{selectedService.warranty_months} muaj</span></p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
              Mbyll
            </Button>
            <Button onClick={() => { setIsViewModalOpen(false); openEditModal(selectedService); }}>
              <Edit className="w-4 h-4 mr-2" />
              Modifiko
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
